import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";
import { columnFilterSchema } from "../../common/columnFilterSchema";
import { statsSchema } from "../../common/statsSchema";
import { merchStatsSchema } from "./v4Query";

export const productReportApiDefinitions = prefixApi(
  "/api",
  apiBuilder({
    alias: "getV4ProductReportByProduct",
    method: "get",
    path: "/v4/query/?table=product&grouping=product_id",
    parameters: [
      {
        name: "search",
        type: "Query",
        schema: z.string().array().optional(),
      },
      {
        name: "marketplace",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "start_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "start_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "end_date_previous",
        type: "Query",
        schema: z.string().date(),
      },
      {
        name: "filters",
        type: "Query",
        schema: z
          .array(
            z.object({
              id: z.string(),
              value: z.object({
                op: z.string().transform((op) => {
                  return op;
                }),
                value: z.unknown(),
              }),
            }),
          )
          .optional(),
      },
      {
        name: "cursor",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "ordering",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    status: 200,
    response: z.object({
      data: z.array(
        z
          .object({
            product_id: z.number(),
            product_title: z.string(),
            product_key: z.string(),
            asin: z.string(),
            sku: z.string(),
            sales_state: z.string(),
            inventory_state: z.string(),
            stock_status: z.string(),
            category_id: z.number(),
            product_quantity: z.number(),
            product_quantity_fba: z.number(),
            product_price: z.number(),
            min_price: z.number().nullable(),
            max_price: z.number().nullable(),
            parent_asin: z.string().nullable(),
            primary_variant: z.boolean().nullable(),
            pricing_plan_status: z.enum(["not_enrolled", "enabled", "disabled", ""]).nullable(),
            price_override_end: z.string().nullable(),
            has_ads: z.boolean(),
            tags: z.string().transform((tags) => {
              try {
                return JSON.parse(tags) as string[];
              } catch {
                return [];
              }
            }),
          })
          .merge(statsSchema)
          .merge(merchStatsSchema),
      ),
      cursor: z
        .object({
          hasPreviousPage: z.boolean(),
          hasNextPage: z.boolean(),
          startCursor: z.string().nullable(),
          endCursor: z.string().nullable(),
        })
        .nullable(),
      count: z.number(),
    }),
  })
    .addEndpoint({
      alias: "getV4ProductReportByDate",
      method: "get",
      path: "/v4/query/?table=product&grouping=start_date&limit=1000000&include_count=false",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "date_group",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.array(
          z
            .object({
              start_date: z.string().date(),
            })
            .merge(statsSchema)
            .merge(merchStatsSchema),
        ),
        cursor: z
          .object({
            hasPreviousPage: z.boolean(),
            hasNextPage: z.boolean(),
            startCursor: z.string().nullable(),
            endCursor: z.string().nullable(),
          })
          .nullable(),
        count: z.number(),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportTotals",
      method: "get",
      path: "/v4/query/aggregate/?table=product",
      parameters: [
        {
          name: "search",
          type: "Query",
          schema: z.string().array().optional(),
        },
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "start_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date_previous",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: z
            .array(
              z.object({
                id: z.string(),
                value: z.object({
                  op: z.string().transform((op) => {
                    return op;
                  }),
                  value: z.unknown(),
                }),
              }),
            )
            .optional(),
        },
      ],
      status: 200,
      response: z.object({
        data: statsSchema.merge(merchStatsSchema),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportOptions",
      method: "get",
      path: "/v4/query/values/?table=product",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
      ],
      status: 200,
      response: z.object({
        data: z.object({
          tags: z.array(z.string()),
          variations: z.record(z.array(z.string())), // Dynamic object with keys mapping to arrays of strings
        }),
      }),
    })
    .addEndpoint({
      alias: "getV4ProductReportExport",
      method: "get",
      path: "/v4/query/?table=product&xls=true&report_type=product_report",
      parameters: [
        {
          name: "marketplace",
          type: "Query",
          schema: z.string(),
        },
        {
          name: "start_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "end_date",
          type: "Query",
          schema: z.string().date(),
        },
        {
          name: "filters",
          type: "Query",
          schema: columnFilterSchema,
        },
      ],
      status: 200,
      response: z.instanceof(Blob),
    })
    .build(),
);
